/**
 * A lightweight section renderer using the Shopify Section Rendering API.
 *
 * Replaces Horizon's morph-based renderer with a simple `replaceWith()` strategy.
 * Trade-off: full DOM replacement loses focus/scroll inside the section,
 * but is dramatically simpler and removes the morph.js dependency.
 *
 * Public API kept identical to Horizon's section-renderer for drop-in use.
 */

const SECTION_ID_PREFIX = "shopify-section-";

class SectionRenderer {
  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {Map<string, AbortController>} */
  #abortControllersBySectionId = new Map();

  /** @type {Map<string, Promise<string>>} */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener("load", this.#cachePageSections.bind(this));
  }

  /**
   * Fetches and replaces a section in the DOM.
   * @param {string} sectionId
   * @param {Object} [options]
   * @param {boolean} [options.cache]
   * @param {URL} [options.url]
   * @returns {Promise<string>}
   */
  async renderSection(sectionId, options) {
    const { cache = !window.Shopify?.designMode } = options ?? {};
    const { url } = options ?? {};

    this.#abortPending(sectionId);
    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);
      replaceSection(sectionId, sectionHTML);
    }

    return sectionHTML;
  }

  #abortPending(sectionId) {
    const existing = this.#abortControllersBySectionId.get(sectionId);
    if (existing) existing.abort();
  }

  /**
   * Fetches a section's HTML (with caching and dedup of in-flight requests).
   * @param {string} sectionId
   * @param {boolean} [useCache]
   * @param {URL} [url]
   * @returns {Promise<string>}
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cached = this.#cache.get(sectionUrl);
      if (cached) return cached;
    }

    pendingPromise = fetch(sectionUrl).then((response) => response.text());
    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);
    this.#cache.set(sectionUrl, sectionHTML);

    return sectionHTML;
  }

  #cachePageSections() {
    for (const section of document.querySelectorAll(".shopify-section")) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.has(url)) continue;
      this.#cache.set(url, section.outerHTML);
    }
  }
}

/**
 * @param {string} sectionId
 * @param {URL} [url]
 * @returns {string}
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  const sectionUrl = new URL(url);
  sectionUrl.searchParams.set("section_id", normalizeSectionId(sectionId));
  sectionUrl.searchParams.sort();
  return sectionUrl.toString();
}

/**
 * @param {string} sectionId
 * @returns {string}
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * @param {string} sectionId
 * @returns {string}
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), "");
}

/**
 * Replaces an existing section with new HTML returned by the Section Rendering API.
 *
 * Simple strategy: parse the response, find the matching section node, swap it in.
 * No morphing - children are fully recreated. Web Components re-attach naturally
 * because their `connectedCallback` fires when the new nodes enter the DOM.
 *
 * @param {string} sectionId
 * @param {string} html
 */
export function replaceSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, "text/html");
  const selector = buildSectionSelector(sectionId);
  const existing = document.getElementById(selector);
  const replacement = fragment.getElementById(selector);

  if (!existing) throw new Error(`Section ${sectionId} not found in the DOM`);
  if (!replacement) throw new Error(`Section ${sectionId} not found in the response`);

  existing.replaceWith(replacement);
}

export const sectionRenderer = new SectionRenderer();
